import { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Field, Input, Panel, PanelBody, PanelHeader, SectionLabel } from '../../ui/index.js';
import { money } from './presentation.js';
import type { ShopOrderDetail } from './types.js';

export interface LineEdit {
  readonly qty: string;
  readonly price: string;
  readonly remove: boolean;
}

export interface OrderEditorProps {
  readonly detail: ShopOrderDetail;
  readonly saving: boolean;
  readonly error: string | null;
  readonly warnings: readonly string[];
  readonly preview: PreviewResult | null;
  readonly onPreview: (patch: EditorPatch) => void;
  readonly onSave: (patch: EditorPatch) => void;
  readonly onCancel: () => void;
}

export interface PreviewResult {
  readonly diff: readonly string[];
  readonly totalBefore: string | null;
  readonly totalAfter: string | null;
}

export interface EditorPatch {
  readonly expectedDateModified: string | null;
  readonly syncShipping: boolean;
  readonly contact: Record<string, string>;
  readonly lineItems: { index: number; qty?: number; price?: number; remove?: boolean }[];
}

function initialLineEdits(detail: ShopOrderDetail): Record<number, LineEdit> {
  const entries: Record<number, LineEdit> = {};
  detail.order.lineItems.forEach((item, index) => {
    entries[index] = {
      qty: String(item.qty),
      price: item.price === null ? '' : String(item.price),
      remove: false,
    };
  });
  return entries;
}

export function OrderEditor({
  detail,
  saving,
  error,
  warnings,
  preview,
  onPreview,
  onSave,
  onCancel,
}: OrderEditorProps): React.ReactElement {
  const order = detail.order;
  const [contact, setContact] = useState<Record<string, string>>({
    billingPhone: order.billingPhone ?? '',
    billingAddress: order.billingAddress ?? '',
    billingCity: order.billingCity ?? '',
    billingGov: order.billingGov ?? '',
  });
  const [customerNote, setCustomerNote] = useState('');
  const [syncShipping, setSyncShipping] = useState(true);
  const [lines, setLines] = useState<Record<number, LineEdit>>(() => initialLineEdits(detail));

  const lineItems = useMemo(
    () =>
      Object.entries(lines)
        .map(([key, edit]) => {
          const index = Number(key);
          if (edit.remove) return { index, remove: true };
          const patch: { index: number; qty?: number; price?: number } = { index };
          const source = order.lineItems[index];
          if (edit.qty !== '' && Number(edit.qty) !== source?.qty) patch.qty = Number(edit.qty);
          if (edit.price !== '' && Number(edit.price) !== source?.price) patch.price = Number(edit.price);
          return patch;
        })
        .filter((patch) => 'remove' in patch || 'qty' in patch || 'price' in patch),
    [lines, order.lineItems],
  );

  const dirty =
    Object.entries(contact).some(([key, value]) => {
      const original =
        key === 'billingPhone'
          ? (order.billingPhone ?? '')
          : key === 'billingAddress'
            ? (order.billingAddress ?? '')
            : key === 'billingCity'
              ? (order.billingCity ?? '')
              : (order.billingGov ?? '');
      return value !== original;
    }) || customerNote !== '' || lineItems.length > 0;

  const submit = (dryRun: boolean): void => {
    const changedContact: Record<string, string> = {};
    for (const [key, value] of Object.entries(contact)) {
      if (value.trim() !== '') changedContact[key] = value;
    }
    if (customerNote.trim() !== '') changedContact.customerNote = customerNote;
    const payload = {
      expectedDateModified: order.dateModified,
      syncShipping,
      contact: changedContact,
      lineItems,
      dryRun,
    };
    if (dryRun) onPreview(payload);
    else onSave(payload);
  };

  const setLine = (index: number, patch: Partial<LineEdit>): void => {
    setLines((current) => {
      const existing = current[index] ?? { qty: '', price: '', remove: false };
      const merged: LineEdit = {
        qty: patch.qty ?? existing.qty,
        price: patch.price ?? existing.price,
        remove: patch.remove ?? existing.remove,
      };
      return { ...current, [index]: merged };
    });
  };

  return (
    <Panel>
      <PanelHeader
        index="✎"
        title="Modifier la commande"
        hint="Écrit sur WooCommerce — la synchronisation ne l'écrasera pas."
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
              Annuler
            </Button>
            <Button size="sm" variant="ghost" onClick={() => submit(true)} disabled={saving || !dirty}>
              Prévisualiser
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => submit(false)}
              disabled={saving || !dirty || preview === null}
              title={preview === null ? 'Prévisualisez avant d\'appliquer' : undefined}
            >
              {saving ? 'Envoi…' : 'Appliquer'}
            </Button>
          </>
        }
      />
      <PanelBody className="flex flex-col gap-4">
        {error !== null && <Alert tone="alarm">{error}</Alert>}
        {warnings.map((warning) => (
          <Alert key={warning} tone="warn">
            {warning}
          </Alert>
        ))}

        <SectionLabel>Contact &amp; livraison</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Téléphone" htmlFor="ed-phone" required>
            <Input
              id="ed-phone"
              value={contact.billingPhone ?? ''}
              onChange={(event) => setContact({ ...contact, billingPhone: event.target.value })}
            />
          </Field>
          <Field label="Gouvernorat" htmlFor="ed-gov" hint="Requis par Jetpack à l'expédition.">
            <Input
              id="ed-gov"
              value={contact.billingGov ?? ''}
              onChange={(event) => setContact({ ...contact, billingGov: event.target.value })}
            />
          </Field>
          <Field label="Adresse" htmlFor="ed-address" className="col-span-2">
            <Input
              id="ed-address"
              value={contact.billingAddress ?? ''}
              onChange={(event) => setContact({ ...contact, billingAddress: event.target.value })}
            />
          </Field>
          <Field label="Ville" htmlFor="ed-city">
            <Input
              id="ed-city"
              value={contact.billingCity ?? ''}
              onChange={(event) => setContact({ ...contact, billingCity: event.target.value })}
            />
          </Field>
          <Field label="Note client Woo" htmlFor="ed-note">
            <Input
              id="ed-note"
              value={customerNote}
              placeholder="Ajoutée à la commande"
              onChange={(event) => setCustomerNote(event.target.value)}
            />
          </Field>
        </div>
        <Checkbox
          checked={syncShipping}
          onChange={(event) => setSyncShipping(event.target.checked)}
          label="Reporter les mêmes valeurs sur l'adresse de livraison"
        />

        <SectionLabel>Lignes</SectionLabel>
        <ul className="flex flex-col gap-1.5">
          {order.lineItems.map((item, index) => {
            const edit = lines[index] ?? { qty: String(item.qty), price: '', remove: false };
            return (
              <li
                key={`${item.sku ?? item.name ?? index}-${index}`}
                className={`flex items-center gap-2 rounded-lg border border-border px-2 py-1.5 ${
                  edit.remove ? 'bg-rust-600/5 opacity-60' : 'bg-card'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-ink">{item.name ?? 'Article'}</p>
                  <p className="font-mono text-[10px] text-ink-faint">{item.sku ?? '—'}</p>
                </div>
                <label className="flex items-center gap-1 text-[11px] text-ink-faint">
                  Qté
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={edit.qty}
                    disabled={edit.remove}
                    onChange={(event) => setLine(index, { qty: event.target.value })}
                    className="w-16 px-2 py-1 text-center tabular-nums"
                  />
                </label>
                <label className="flex items-center gap-1 text-[11px] text-ink-faint">
                  PU
                  <Input
                    type="number"
                    min={0}
                    step="0.001"
                    value={edit.price}
                    placeholder={item.price === null ? '—' : String(item.price)}
                    disabled={edit.remove}
                    onChange={(event) => setLine(index, { price: event.target.value })}
                    className="w-24 px-2 py-1 text-right tabular-nums"
                  />
                </label>
                <Checkbox
                  checked={edit.remove}
                  onChange={(event) => setLine(index, { remove: event.target.checked })}
                  label="✕"
                />
              </li>
            );
          })}
          {order.lineItems.length === 0 && (
            <li className="px-1 py-2 text-xs text-ink-faint">
              Aucune ligne — cette commande anormale ne peut pas être éditée ici.
            </li>
          )}
        </ul>
        <p className="text-[11px] text-ink-faint">
          Le total n'est jamais saisi : WooCommerce le recalcule après chaque ligne modifiée. Actuel{' '}
          {money(order.total)}.
        </p>

        {preview !== null && (
          <div className="rounded-lg border border-sky-600/30 bg-sky-600/5 px-3 py-2">
            <SectionLabel>Aperçu sur WooCommerce</SectionLabel>
            <ul className="flex flex-col gap-0.5">
              {preview.diff.map((line) => (
                <li key={line} className="text-[11px] text-ink-soft">{line}</li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] tabular-nums text-ink-faint">
              Total attendu : {preview.totalBefore ?? '—'} → {preview.totalAfter ?? '—'} (estimation ; Woo
              tranche)
            </p>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}